import {
  BLUE_300,
  BLUE_900,
  CLEMENTINE_300,
  CLEMENTINE_900,
  GRAY_300,
  GRAY_700,
  GRAY_900,
  GREEN_300,
  GREEN_900,
  INK_300,
  INK_900,
  PLUM_300,
  PLUM_900,
  PURPLE_300,
  PURPLE_900,
  RASPBERRY_300,
  RASPBERRY_900,
  RED_300,
  RED_900,
  TEAL_300,
  TEAL_900,
  YELLOW_300,
  YELLOW_900,
} from 'cio/utils/colors';
import { lightenOrDarkenColor } from 'cio/utils/color-functions';
import VisualWorkflowBuilderV2 from 'cio/components/visual-workflow-builder-v2';
import { getOwner, setOwner } from '@ember/application';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import ApiService from 'cio/services/api';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { FlyDragEvent } from 'fly-drag-drop/utils/events';
import AnalyticsService from 'cio/services/analytics';
import CampaignModel from 'cio/models/campaign';
import { VWBComponentWrapper } from 'cio/utils/visual-workflow-builder/context';
import CampaignNoteModel from 'cio/models/campaign-note';
import DS from 'ember-data';

export const COLORS = [
  YELLOW_300,
  TEAL_300,
  PURPLE_300,
  PLUM_300,
  GREEN_300,
  BLUE_300,
  RED_300,
  INK_300,
  RASPBERRY_300,
  CLEMENTINE_300,
  GRAY_300,
];

// maps the text colors based on background color
// some colors need to be darkened for the text to be accessible
export const TEXT_COLOR_MAP: Record<string, string> = {
  [YELLOW_300]: GRAY_700,
  [TEAL_300]: GRAY_700,
  [PURPLE_300]: GRAY_900,
  [PLUM_300]: GRAY_700,
  [GREEN_300]: GRAY_700,
  [BLUE_300]: GRAY_700,
  [RED_300]: GRAY_900,
  [INK_300]: GRAY_700,
  [RASPBERRY_300]: GRAY_700,
  [CLEMENTINE_300]: GRAY_700,
  [GRAY_300]: GRAY_700,
};

export const META_TEXT_COLOR_MAP: Record<string, string> = {
  [YELLOW_300]: lightenOrDarkenColor(YELLOW_900, -2),
  [TEAL_300]: TEAL_900,
  [PURPLE_300]: PURPLE_900,
  [PLUM_300]: PLUM_900,
  [GREEN_300]: GREEN_900,
  [BLUE_300]: BLUE_900,
  [RED_300]: RED_900,
  [INK_300]: INK_900,
  [RASPBERRY_300]: RASPBERRY_900,
  [CLEMENTINE_300]: CLEMENTINE_900,
  [GRAY_300]: GRAY_900,
};

export const DEFAULT_BG_COLOR = YELLOW_300;

export const NOTE_SIZE = 200;

export type StickyNoteUser = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
};

type TemporaryNote = {
  id: string;
  x: number;
  y: number;
  content: string;
  color: string;
};

export function createStickyNotesManagerForComponent(vwbComponent: VisualWorkflowBuilderV2) {
  let wrapper = new VWBComponentWrapper(vwbComponent);
  let owner = getOwner(vwbComponent);

  return new StickyNotesManager(wrapper, owner);
}

export interface StickyNotesManagerParams {
  campaign: CampaignModel;
  inViewport: boolean;
  loaded: boolean;
  isCopyingFrom: boolean;
  isCopyingTo: boolean;
  isMinimapMode: boolean;
  isReadOnly: boolean;
  zoomLevel: number;
  graphElement: HTMLElement | null;
  stickyNotesEnabled: boolean;
}

export class StickyNotesManager {
  params: StickyNotesManagerParams;

  constructor(params: StickyNotesManagerParams, owner: any) {
    this.params = params;

    setOwner(this, owner);

    if (
      !this.params.stickyNotesEnabled ||
      this.params.isCopyingFrom ||
      this.params.isCopyingTo ||
      this.params.isMinimapMode
    ) {
      return;
    }

    this.fetchNotes.perform();
  }

  @service analytics!: AnalyticsService;
  @service api!: ApiService;
  @service store!: DS.Store;

  @tracked notes: CampaignNoteModel[] = [];
  @tracked temporaryNotes: TemporaryNote[] = [];

  @tracked stickyNoteCount = 0;

  get canShowStickyNotes() {
    return (
      this.params.stickyNotesEnabled &&
      this.params.inViewport &&
      this.params.loaded &&
      !(this.params.isCopyingFrom ?? false) &&
      !(this.params.isCopyingTo ?? false) &&
      !(this.params.isMinimapMode ?? false)
    );
  }

  get canCreateOrEditNote() {
    return !this.params.isReadOnly;
  }

  @tracked isShowingStickyNotes = true;

  @action
  toggleStickyNotes() {
    this.isShowingStickyNotes = !this.isShowingStickyNotes;
  }

  fetchNotes = task({ drop: true }, async () => {
    let notes = (await this.store.query('campaign-note', { campaign_id: this.params.campaign.id })).toArray();

    this.notes = notes;

    this.stickyNoteCount = notes.length;
  });

  addNote = task(async (coordinates: { x: number; y: number }) => {
    let newNote = this.store.createRecord('campaign-note', {
      ...coordinates,
      content: '',
      color: DEFAULT_BG_COLOR,
      campaign: this.params.campaign,
    });

    let id = window.UUID.generate();

    this.temporaryNotes = [
      ...this.temporaryNotes,
      { id, x: newNote.x, y: newNote.y, content: newNote.content, color: newNote.color },
    ];
    this.stickyNoteCount = this.notes.length + this.temporaryNotes.length;

    let result = await newNote.save();

    this.notes = [...this.notes, result];
    this.temporaryNotes = this.temporaryNotes.filter((n) => n.id !== id);
  });

  deleteNote = task(async (noteId: string | number) => {
    await this.store.peekRecord('campaign-note', noteId)?.destroyRecord();

    this.notes = this.notes.filter((n) => n.id !== noteId);
    this.stickyNoteCount = this.notes.length;
  });

  @action
  onDragStartNew() {
    this.analytics.track('campaign', 'add_note_started', {
      model: this.params.campaign,
    });
  }

  @action
  onDragEndNew(e: FlyDragEvent) {
    if (e.detail.clientX == null || e.detail.clientY == null || this.params.graphElement == null) {
      return;
    }

    let rect = this.params.graphElement.getBoundingClientRect();

    let scale = this.params.zoomLevel / 100;

    let coordinates = {
      x: Math.round((e.detail.clientX - rect.x) / scale),
      y: Math.round((e.detail.clientY - rect.y) / scale),
    };

    this.addNote.perform(coordinates);
  }

  @action
  initialOffset() {
    return {
      x: 100,
      y: 100,
    };
  }
}
